import React, { useState } from "react";
import { useNavigate as useNav } from "react-router-dom";
import { ArrowLeft, Info, FileText, Calendar, User, Clock } from "lucide-react";
import { CardTitle } from "@shared/ui/card";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { PageHeader } from "@shared/components/PageHeader";
import { Tabs } from "@shared/ui/tabs";
import { Button } from "@shared/ui/button";
import OverviewTab from "./tabs/OverviewTab";
import SubResourcesTab from "./tabs/SubResourcesTab";
import ActivitiesTab from "./tabs/ActivitiesTab";
import AllocationTab from "./tabs/AllocationTab";
import { TableColumn } from "@shared/types/table";
import { WorkOrdersTab } from "@features/mx/shared/components/WorkOrdersTab";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterWorkorderInfo } from "@features/mx/resources/hook";
import { NotesTab } from "@features/mx/shared/components/NotesTab";
import LogsTab from "./tabs/LogsTab";
import { UpdateUnitModal } from "./tabs/UpdateUnitModal";
import { UpdatePhysicalStatusModal } from "./tabs/UpdatePhysicalStatusModal";
import { NoteDialog } from "@features/mx/shared/components/NoteDialog";
import { CreateServiceOrderModal } from "./tabs/serviceOrderModal";
import { UpdateConsumerModal } from "./tabs/updateConsumerModal";

interface ResourceDetailProps {
  resource: any;
}

export default function ResourceDetail({ resource }: ResourceDetailProps) {
  const navigate = useNav();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: workOrdersData } = useMeterWorkorderInfo({
    meterId: resource?.id,
    remoteUtilityId,
  });

  const [isUpdateUnitOpen, setIsUpdateUnitOpen] = useState(false);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isServiceOrderOpen, setIsServiceOrderOpen] = useState(false);
  const [isUpdateConsumerOpen, setIsUpdateConsumerOpen] = useState(false);

  // Determine status configurations
  const isInstalled = resource?.physicalStatus === "Installed" || resource?.physicalStatusLabel === "Installed";

  const isMaster = resource?.ownership === "0";

  // Quick Action List
  const quickActions = [
    {
      label: "Edit Details",
      icon: <FileText className="w-4 h-4" />,
      onClick: () => {
        const isWarehouse = resource?.physicalStatus === "In Warehouse" || resource?.physicalStatusLabel === "In Warehouse";
        const pathType = isWarehouse ? "warehouse" : "installed";
        navigate(`/mx/manage/resources/edit/${pathType}/${resource?.id}`);
      }
    },
    { label: "Create Service Order", icon: <Calendar className="w-4 h-4" />, onClick: () => setIsServiceOrderOpen(true) },
    { label: "Update Consumer", icon: <User className="w-4 h-4" />, onClick: () => setIsUpdateConsumerOpen(true) },
    { label: "Add Note", icon: <FileText className="w-4 h-4" />, onClick: () => setIsNoteDialogOpen(true) },
    { label: "Update Unit", icon: <Clock className="w-4 h-4" />, onClick: () => setIsUpdateUnitOpen(true) },
    { label: "Update Physical Status", icon: <Clock className="w-4 h-4" />, onClick: () => setIsUpdateStatusOpen(true) },
  ];


  // Work Orders Columns definition
  const workOrdersColumns: TableColumn<any>[] = [
    { key: "jobCardNo", header: "Work order id", type: "text" },
    { key: "sopAssociationTypeDisplay", header: "Type", type: "text" },
    { key: "remark", header: "Description", type: "text" },
    {
      key: "priority",
      header: "Priority",
      type: "text",
      render: (val) => {
        let colors = "bg-gray-100 text-gray-800 border-gray-255";
        if (val === "High") colors = "bg-red-50 text-red-700 border-red-200";
        if (val === "Medium") colors = "bg-amber-50 text-amber-700 border-amber-200";
        return (
          <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${colors}`}>
            {val ?? 'NA'}
          </span>
        );
      }
    },
    { key: "fieldForceName", header: "Assigned to", type: "text" },
    {
      key: "createdUserRemoteName",
      header: "Created",
      type: "text",
      render: (_, row: any) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.createdUserRemoteName || 'NA'}</span>
          <span className="text-xs text-gray-500">{row.createdDate || row.createdOn || 'NA'}</span>
        </div>
      )
    },
    { key: "assignDate", header: "Due date", type: "text" },
    {
      key: "statusDisplay",
      header: "Status",
      type: "text",
      render: (val) => {
        let colors = "bg-gray-50 text-gray-700 border-gray-200";
        if (val === "Completed") colors = "bg-green-50 text-green-700 border-green-200";
        if (val === "In Progress") colors = "bg-blue-50 text-blue-700 border-blue-200";
        if (val === "Open") colors = "bg-amber-50 text-amber-700 border-amber-200";
        return (
          <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${colors}`}>
            {val}
          </span>
        );
      }
    }
  ];

  const tabComponents = {
    overview: {
      label: "Overview",
      component: <OverviewTab resource={resource} />,
    },
    ...(isMaster ? {
      subResources: {
        label: "Sub Resources",
        component: (
          <SubResourcesTab
            resource={resource}
          />
        ),
      }
    } : {}),
    activities: {
      label: "Activities",
      component: <ActivitiesTab />,
    },
    allocation: {
      label: "Allocation",
      component: <AllocationTab resource={resource} isAssigned={resource?.status?.toLowerCase() === 'assigned'} />,
    },
    workOrders: {
      label: "Work Orders",
      component: (
        <WorkOrdersTab
          workOrdersData={workOrdersData ?? []}
          workOrdersColumns={workOrdersColumns}
        />
      ),
    },
    notes: {
      label: "Notes",
      component: (
        <NotesTab
          requestId={resource?.id || ""}
          source="1"
          title="Resource Notes"
        />
      ),
    },
    activityLogs: {
      label: "Activity Logs",
      component: <LogsTab resource={resource} />,
    },
  };

  const urlMapping = {
    overview: "overview",
    ...(isMaster ? { subResources: "sub-resources" } : {}),
    activities: "activities",
    allocation: "allocation",
    workOrders: "work-orders",
    notes: "notes",
    activityLogs: "activity-logs",
  };

  return (
    <div>
      {/* Header section with PageHeader */}
      <PageHeader
        title="Resource Detail"
        subtitle="Manage resource information and status details"
        icon={ArrowLeft}
        onIconClick={() => navigate("/mx/manage/resources")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start mt-4">
        {/* Main Info Columns */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm space-y-4">
            {/* Header Block */}
            <div className="flex flex-col md:flex-row justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 text-blue-600">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-lg font-bold text-gray-900 mt-1">{resource?.meterNumber || resource?.id}</h2>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-150">
                      Resource type: {resource?.resourceTypeDisplay || "NA"}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-150">
                      Sub type: {resource?.resourceSubTypeDisplay || "NA"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Panels */}
              <div className="border border-gray-150 bg-gray-50/50 rounded-xl p-4 flex gap-6 min-w-[340px]">
                <div
                  className="flex flex-col justify-between flex-1 cursor-pointer hover:bg-gray-100/50 p-1 rounded-lg transition-all"
                // onClick={() => setIsUpdateStatusOpen(true)}
                >
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>Physical Status</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-sm font-semibold text-gray-800">
                    <span className={`w-2.5 h-2.5 rounded-full ${isInstalled ? "bg-green-500" : "bg-red-500"}`} />
                    <span>{resource?.physicalStatus || resource?.physicalStatusLabel || "NA"}</span>
                  </div>
                </div>

                <div className="w-px bg-gray-250 self-stretch" />

                <div className="flex flex-col justify-between flex-1 p-1 rounded-lg">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>Assignment Status</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-sm font-semibold text-gray-800">
                    <span className={`w-2.5 h-2.5 rounded-full ${resource?.status?.toLowerCase() === 'assigned' ? "bg-green-500" : "bg-gray-400"}`} />
                    <span>{resource?.status}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Resource Overview */}
            <div>
              <CardTitle className="text-sm font-semibold text-gray-855 mb-4 pb-2">
                Resource Overview
              </CardTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
                <ValueLabel label="Utility Service" value={resource?.productCode || "NA"} />
                <ValueLabel label="Resource Classification" value={resource?.ownershipDisplay || 'NA'} />
                <ValueLabel label="Installation Date" value={resource?.installedOn || "NA"} />
                <ValueLabel label="Category" value={resource?.categoryName || "NA"} />
                <ValueLabel label="Sub-Category" value={resource?.subCategoryName || "NA"} />
                <ValueLabel label="Manufacturer" value={resource?.manufacturerName || "NA"} />
                <ValueLabel label="Model" value={resource?.modelName || "NA"} />
              </div>
            </div>

            {/* Audit */}
            <div className="border-t border-gray-100 pt-6">
              <CardTitle className="text-sm font-semibold text-gray-855 mb-4 pb-2 ">
                Audit
              </CardTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
                <ValueLabel label="Created By" value={resource?.createdBy || "NA"} />
                <ValueLabel label="Created On" value={resource?.createdOn || resource?.createdDate || "NA"} />
                <ValueLabel label="Last Updated By" value={resource?.lastUpdatedBy || "NA"} />
                <ValueLabel label="Last Updated On" value={resource?.lastUpdatedOn || "NA"} />
              </div>
            </div>
          </div>

        </div>

        {/* Quick Actions Panel (Right Column) */}
        <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm space-y-4 lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-850 flex items-center justify-between">
            <span>Quick Actions</span>
          </h3>
          <div className="flex flex-col gap-2 pt-2">
            {quickActions.map((action, idx) => (
              <Button
                key={idx}
                onClick={action.onClick}
                variant="outline"
                className="justify-start gap-3 w-full"
              >
                {action.icon}
                <span>{action.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Shared Tab Component */}
      <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm mt-4">
        <Tabs
          defaultValue="overview"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListClassName={`grid w-full grid-cols-${isMaster ? '7' : '6'} mb-6`}
          idPrefix="resource-detail-tab"
          level={0}
        />
      </div>

      <UpdateUnitModal
        open={isUpdateUnitOpen}
        onOpenChange={setIsUpdateUnitOpen}
        resource={resource}
      />

      <UpdatePhysicalStatusModal
        open={isUpdateStatusOpen}
        onOpenChange={setIsUpdateStatusOpen}
        resource={resource}
      />

      <NoteDialog
        isOpen={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        itemId={resource?.id || ""}
        source="1"
        title="Add Resource Note"
      />

      <CreateServiceOrderModal
        isOpen={isServiceOrderOpen}
        onClose={() => setIsServiceOrderOpen(false)}
        resource={resource}
      />

      <UpdateConsumerModal
        open={isUpdateConsumerOpen}
        onOpenChange={setIsUpdateConsumerOpen}
        resource={resource}
      />
    </div>
  );
}
